import { Account, AccountDivisionType, GuaranteedType, SecuritySymbol, TradingPlatform } from '@tdwb/core';

export type EnrollmentStatus = 'ENROLLED' | 'IN_PROGRESS' | 'ELIGIBLE' | 'INELIGIBLE';

export type LoanStatus = 'DEFAULT' | 'RECALLING' | 'ON_LOAN' | 'EXCLUDE';

export type EarningsHistorySubTab = 'monthlyPayments' | 'bySecurity';

export type StockLendingTabType = 'onLoan' | 'earnings' | 'manage';

export type FplEnrollmentStep = 'INTRODUCTION' | 'SELECT_ACCOUNTS' | 'REVIEW' | 'SUCCESS';

export const EnrollmentStepToModalNameMapping: { [key in FplEnrollmentStep]: string; } = {
  INTRODUCTION: 'intro',
  SELECT_ACCOUNTS: 'account-selection',
  REVIEW: 'review',
  SUCCESS: 'success'
};

export const StockLendingTabMapping: { [key in StockLendingTabType]: string } = {
  onLoan: 'on loan',
  earnings: 'earnings history',
  manage: 'manage'
};

export const StockLendingEarningsTabMapping: { [key in EarningsHistorySubTab]: string } = {
  monthlyPayments: 'monthly',
  bySecurity: 'security'
};

export interface FplSecurityEarnings extends FplSecurity {
  interestEarnedThisMonth: number;
  totalInterestPaid: number;
}

export interface FplSecurity {
  symbol: SecuritySymbol;
  cusip: string;
  name: string;
  underlyingSecurityId: string;
  restricted: boolean;
  recalling: boolean;
  onLoan: boolean;
  loanStatus: LoanStatus;
  logoLight?: string;
  logoDark?: string;
  quantity?: number;
}

export type RestrictionType = 'ACCOUNT' | 'SECURITY';

export interface RetrieveRestrictions {
  percent: number;
  accountRestricted: boolean;
  accountRuleId: number;
  securityRuleId: number;
  excludedStocks: number;
  fplSecurities: FplSecurities;
}

export interface FplSecurities {
  fplHoldings: Array<FplSecurity>;
  restrictedSecurities?: Array<string>;
}

export interface CreateRestrictions {
  restrictionType: RestrictionType;
  accountId: string;
  securities?: Array<string>;
  recall?: boolean;
}

export interface CreateLimitRestrictions {
  accountId: string;
  amountLimit: number;
}

export interface StockLoanDetails {
  pendingRecall: boolean;
  initialRateMin: number;
  initialRateMax: number;
  currentRateMin: number;
  currentRateMax: number;
}

export interface MonthlyPayment {
  dateDeposited: Date;
  interestStartDate: Date;
  interestEndDate: Date;
  interestPaid: number;
}

export interface MonthlyPaymentHistory {
  monthlyPayments: Array<MonthlyPayment>;
  totalInterestAmount: number;
}

export interface FplDisclosure {
  fplDisclosure: string;
}

export interface AnnualPaymentHistory {
  year: number;
  totalInterest: number;
  monthlyPayments: Array<MonthlyPayment>;
}

export interface FplOnLoanHolding extends FplSecurity {
  onLoanQuantity: number;
  onLoanMarketValue: number;
  interestEarnedThisMonth: number;
}

export interface FplOnLoanDetails {
  totalOnLoanMarketValue: number;
  totalInterestEarnedThisMonth: number;
  accountRestricted: boolean;
  eligibleSecurities: boolean;
  onLoanSecurities: Array<FplOnLoanHolding>;
}

export interface StockLendingTabDetails {
  nextPayDate: Date;
  interestEarnedThisMonth: number;
}

export interface FplAccountGroups {
  [accountNumber: string]: FplAccountGroup;
}

export interface FplAccountGroup {
  [accountId: string]: FplAccount;
}

export interface FplAccount extends Account {
  businessLine: string;
  cashBalance: number;
  domain?: string;
  securityBalance: number;
  totalAccountValue: number;
  totalAccountBookValue: number;
  marginBalance: number;
  sessionKey: string;
  rrCode?: string;
  isSpousal?: boolean;
  hasFplPairedAccount?: boolean;
  optionTradeAuthorizationCode?: number;
  tradingPlatform: TradingPlatform;
  guaranteedType: GuaranteedType;
  divisionType: AccountDivisionType;
}

export interface FplAccountGroupData {
  selected?: boolean;
  accountIds: Array<string>;
  accounts: Array<FplAccount>;
}
